/**
 * O ROTULO de uma linha da DRE — apelido e operador, resolvidos como o contrato resolve.
 *
 * Esta tela renderizava `serie.props.name` direto, e nunca leu `doc` nem `alias`: renomear uma
 * linha na Customizacao do pro-v2 nao aparecia aqui. O apelido e gravado no documento
 * (`component.configs.series[].alias`, com o `doc` espelhando o rotulo exibido), e e daqui que ele
 * passa a chegar na tela.
 *
 * Espelha `parseOperator` e a resolucao de `resolveRowsFromConfigs` do data-engine. As duas telas
 * mostram o MESMO documento: divergir faz o cliente renomear numa e ver outro nome na outra.
 */

export const DRE_OPERATORS = ["=", "+", "-", "()"];

/**
 * Separa o operador do rotulo.
 *
 * O operador vive DENTRO do texto no dado legado — `(=) Lucro Liquido`, `(-)Impostos Sobre Vendas`.
 *
 * So o PREFIXO conta, e so quando o conteudo entre parenteses e exatamente um operador conhecido.
 * `(RECEITAS/DESPESAS) OPERACIONAIS` NAO tem operador — o parentese ali e parte do nome contabil, e
 * trata-lo como operador apagaria metade do rotulo.
 */
export function parseOperator(rawLabel) {
    const label = String(rawLabel ?? "").trim();
    if (!label.startsWith("(")) return { operator: null, label };
    const close = label.indexOf(")");
    if (close === -1) return { operator: null, label };
    const inner = label.slice(1, close).trim();
    const rest = label.slice(close + 1).trim();
    // `()` so e operador com o parentese VAZIO e sobrando rotulo depois dele.
    if (inner === "" && rest) return { operator: "()", label: rest };
    if (DRE_OPERATORS.indexOf(inner) === -1) return { operator: null, label };
    // `(=)` sem nada depois nao e operador, e o rotulo inteiro.
    if (!rest) return { operator: null, label };
    return { operator: inner, label: rest };
}

/**
 * O texto que a linha exibe.
 *
 * Precedencia, a mesma do contrato: apelido explicito > `doc` gravado > nome da serie. O operador
 * volta na frente do rotulo porque e assim que esta tela sempre mostrou — `(-) Deducoes da Receita
 * Bruta` — e mudar isso agora trocaria a cara da DRE de toda a base.
 *
 * O rotulo base entra DESPIDO do operador antes de recebe-lo de volta: nome de serie ja carrega o
 * operador embutido, e prefixar por cima duplicaria o sinal a cada leitura.
 */
export function resolveRowLabel(config = {}, serieName = "") {
    const base = config.alias || config.doc || serieName;
    const parsed = parseOperator(base);
    if (!parsed.label) return "";
    return operadorDe(config, parsed, serieName)
        ? `(${operadorDe(config, parsed, serieName)}) ${parsed.label}`
        : parsed.label;
}

/**
 * Qual operador a linha usa.
 *
 * Campo explicito vence tudo — e `null` explicito significa SEM operador, nao "deduza do texto".
 *
 * Sem o campo, deduz do texto mais especifico que TIVER um: o apelido, depois o `doc`, depois o
 * nome da serie. Cair direto no apelido perderia o sinal: quem apelida "(-) DEDUCOES DA RECEITA
 * BRUTA" para "Impostos" digitou o texto, nao o sinal — e a linha viraria "Impostos", somando onde
 * deveria subtrair aos olhos de quem le.
 */
function operadorDe(config, parsed, serieName) {
    if (config.operator !== undefined) return config.operator;
    if (parsed.operator) return parsed.operator;
    const candidatos = [config.doc, serieName];
    for (const candidato of candidatos) {
        const { operator } = parseOperator(candidato);
        if (operator) return operator;
    }
    return null;
}
